import { ImageBackground } from 'expo-image';
import { View } from 'react-native';

import {
  Button,
  MqSwitch,
  QuantityCounter,
  ScrollView,
  Skeleton,
  XStack,
  YStack,
  createMqStyles,
  tokens,
  useMqSelect,
} from '@fhs/ui';

import { ProductDetailLayout } from '../product-detail-layout';

interface Props {
  hideFooter?: boolean;
  isModal?: boolean;
}

export const MenuItemSkeletonLoading = ({ hideFooter = false, isModal = false }: Props) => {
  return (
    <MqSwitch
      $ltDesktop={() => <MenuItemSkeletonLoadingMobile isModal={isModal} hideFooter={hideFooter} />}
      $gteDesktop={() => (
        <MenuItemSkeletonLoadingDesktop isModal={isModal} hideFooter={hideFooter} />
      )}
    />
  );
};

const Footer = ({ isModal }) => {
  const mqStyles = useMqStyles();

  return (
    <View style={mqStyles.footerContainer}>
      <View style={mqStyles.footer}>
        <QuantityCounter size="md" value={1} maxValue={10} minValue={1} onChangeValue={() => {}}>
          <QuantityCounter.Stepper />
        </QuantityCounter>
        <Button style={mqStyles.button} disabled>
          <MqSwitch
            $base={() => (
              <View style={mqStyles.buttonContent}>
                <Button.Text style={mqStyles.buttonText}>
                  {isModal ? 'Add Item' : 'Add to Order |'}
                </Button.Text>
                {!isModal && (
                  <Skeleton style={[mqStyles.buttonPriceSkeleton, mqStyles.mobileButtonPrice]} />
                )}
              </View>
            )}
            $gteDesktop={() => (
              <View style={mqStyles.desktopButtonContent}>
                <Button.Text style={mqStyles.buttonText}>
                  {isModal ? 'Add Item' : 'Add to Order'}
                </Button.Text>
                {!isModal && <Skeleton style={mqStyles.buttonPriceSkeleton} />}
              </View>
            )}
          />
        </Button>
      </View>
    </View>
  );
};

const ComboSelectionsSkeleton = () => {
  const mqStyles = useMqStyles();

  return (
    <YStack style={mqStyles.comboSection}>
      <View style={mqStyles.comboTitle} />
      <View style={mqStyles.comboItemsWrapper}>
        {[0, 1].map(index => (
          <XStack
            key={index}
            style={[mqStyles.comboItem, index === 0 && mqStyles.comboItemBorder]}
          >
            <View style={mqStyles.comboItemImage} />
            <YStack style={mqStyles.comboItemText}>
              <View style={mqStyles.comboItemTitle} />
              <View style={mqStyles.comboItemSubtitle} />
            </YStack>
          </XStack>
        ))}
      </View>
    </YStack>
  );
};

const NutritionInformationSkeleton = () => {
  const mqStyles = useMqStyles();

  return (
    <View style={mqStyles.nutritionItem}>
      <View style={mqStyles.nutritionTitle} />
      <View style={mqStyles.nutritionChevron} />
    </View>
  );
};

const MenuItemSkeletonLoadingMobile = ({ hideFooter, isModal }: Props) => {
  const mqStyles = useMqStyles();
  const boxDisplay = !isModal;

  return (
    <View style={mqStyles.container}>
      <ScrollView showsVerticalScrollIndicator={false}>
        <View style={mqStyles.content}>
          <YStack style={mqStyles.titleContainer}>
            <View style={mqStyles.itemTitle} />
            <View style={mqStyles.itemSubtitle} />
          </YStack>
          <ImageBackground
            contentFit={useMqSelect({ $ltDesktop: 'contain' }, 'none')}
            source={require('../../assets/img/menu_item_detail_bg_2477_1058.webp')}
            style={mqStyles.containerImageBackground}
          >
            <View style={mqStyles.imageAnatomy} />
          </ImageBackground>
          <View style={mqStyles.description}>
            <View style={[mqStyles.itemDescriptionLine, { width: '96%' }]} />
            <View style={[mqStyles.itemDescriptionLine, { width: '100%' }]} />
            <View style={[mqStyles.itemDescriptionLine, { width: '84%' }]} />
            <View style={[mqStyles.itemDescriptionLine, { width: '48%' }]} />
          </View>

          <YStack style={mqStyles.mobileCustomizationContent}>
            {boxDisplay && (
              <>
                <YStack style={mqStyles.skeletonSection}>
                  <View style={mqStyles.sectionTitle} />
                  <XStack style={mqStyles.sizeTile}>
                    <View style={mqStyles.sizeBox} />
                    <View style={mqStyles.sizeBox} />
                  </XStack>
                </YStack>
                <YStack style={mqStyles.skeletonSection}>
                  <View style={mqStyles.sectionTitle} />
                  <XStack style={mqStyles.sizeTile}>
                    <View style={mqStyles.sizeBox} />
                    <View style={mqStyles.sizeBox} />
                    <View style={mqStyles.sizeBox} />
                  </XStack>
                </YStack>
              </>
            )}

            <YStack style={mqStyles.skeletonSection}>
              <View style={mqStyles.sectionTitle} />
              <View style={mqStyles.customizeBox} />
            </YStack>

            <XStack style={mqStyles.customizeHeader}>
              <View style={mqStyles.customizeSection}>
                <View style={[mqStyles.itemDescriptionLine, { width: 131 }]} />
                <View style={[mqStyles.itemDescriptionLine, { width: 211 }]} />
              </View>
              <XStack style={mqStyles.allowCombo}>
                <View style={[mqStyles.itemDescriptionLine, { width: 43, height: 12 }]} />
                <View style={[mqStyles.itemDescriptionLine, { width: 48, height: 24 }]} />
              </XStack>
            </XStack>
            <ComboSelectionsSkeleton />
            <View style={mqStyles.contentDivider} />
            <NutritionInformationSkeleton />
          </YStack>
        </View>
      </ScrollView>
      {!hideFooter && <Footer isModal={isModal} />}
    </View>
  );
};

const MenuItemSkeletonLoadingDesktop = ({ hideFooter, isModal }: Props) => {
  const mqStyles = useMqStyles();
  const boxDisplay = !isModal;

  return (
    <View style={mqStyles.container}>
      <View style={mqStyles.flex1}>
        <ProductDetailLayout
          isStaticDetails={false}
          nameAndImageContent={
            <>
              <View style={mqStyles.desktopProductSpacer} />
              <YStack style={mqStyles.desktopProductContent}>
                <View style={[mqStyles.itemDescriptionLine, mqStyles.desktopProductTitle]} />
                <View style={[mqStyles.itemDescriptionLine, mqStyles.desktopProductSubtitle]} />
                <View style={mqStyles.productHeader}>
                  <View style={[mqStyles.itemDescriptionLine, mqStyles.desktopDescriptionLong]} />
                  <View style={[mqStyles.itemDescriptionLine, mqStyles.desktopDescriptionMedium]} />
                  <View style={[mqStyles.itemDescriptionLine, mqStyles.desktopDescriptionShort]} />
                </View>
              </YStack>
              <View style={mqStyles.desktopProductSpacer} />
              <View style={mqStyles.backgroundContainer}>
                <View style={mqStyles.imageAnatomy} />
              </View>
            </>
          }
          customizationContent={
            <YStack style={mqStyles.desktopCustomizationContent}>
              {boxDisplay && (
                <>
                  <YStack style={mqStyles.desktopSkeletonSection}>
                    <View style={mqStyles.sectionTitle} />
                    <XStack style={mqStyles.sizeTile}>
                      <View style={mqStyles.sizeBox} />
                      <View style={mqStyles.sizeBox} />
                    </XStack>
                  </YStack>
                  <YStack style={mqStyles.desktopSkeletonSection}>
                    <View style={mqStyles.sectionTitle} />
                    <XStack style={mqStyles.sizeTile}>
                      <View style={mqStyles.sizeBox} />
                      <View style={mqStyles.sizeBox} />
                      <View style={mqStyles.sizeBox} />
                    </XStack>
                  </YStack>
                </>
              )}
              <YStack style={mqStyles.desktopSkeletonSection}>
                <View style={mqStyles.sectionTitle} />
                <View style={mqStyles.customizeBox}>
                  <XStack style={mqStyles.customizeBoxDesktopRow}>
                    <XStack>
                      <View style={mqStyles.itemImage} />
                      <View style={mqStyles.itemDetails}>
                        <View style={[mqStyles.itemDescriptionLine, { width: 80, height: 18 }]} />
                        <View style={[mqStyles.itemDescriptionLine, { width: 153, height: 12 }]} />
                      </View>
                    </XStack>
                    <View style={mqStyles.selectBox} />
                  </XStack>
                  <View style={mqStyles.divider} />
                  <XStack style={mqStyles.customizeBoxDesktopRow}>
                    <XStack>
                      <View style={mqStyles.itemImage} />
                      <View style={mqStyles.itemDetails}>
                        <View style={[mqStyles.itemDescriptionLine, { width: 80, height: 18 }]} />
                        <View style={[mqStyles.itemDescriptionLine, { width: 153, height: 12 }]} />
                      </View>
                    </XStack>
                    <View style={mqStyles.selectBox} />
                  </XStack>
                  <View style={mqStyles.divider} />
                  <XStack style={mqStyles.customizeBoxDesktopRow}>
                    <XStack>
                      <View style={mqStyles.itemImage} />
                      <View style={mqStyles.itemDetails}>
                        <View style={[mqStyles.itemDescriptionLine, { width: 80, height: 18 }]} />
                        <View style={[mqStyles.itemDescriptionLine, { width: 153, height: 12 }]} />
                      </View>
                    </XStack>
                    <View style={mqStyles.selectBox} />
                  </XStack>
                  <View style={mqStyles.divider} />
                  <XStack style={mqStyles.customizeBoxDesktopRow}>
                    <XStack>
                      <View style={mqStyles.itemImage} />
                      <View style={mqStyles.itemDetails}>
                        <View style={[mqStyles.itemDescriptionLine, { width: 80, height: 18 }]} />
                        <View style={[mqStyles.itemDescriptionLine, { width: 153, height: 12 }]} />
                      </View>
                    </XStack>
                    <View style={mqStyles.selectBox} />
                  </XStack>
                </View>
              </YStack>
              <ComboSelectionsSkeleton />
              <View style={mqStyles.contentDivider} />
              <NutritionInformationSkeleton />
            </YStack>
          }
        />
      </View>
      {!hideFooter && <Footer isModal={isModal} />}
    </View>
  );
};

const useMqStyles = createMqStyles({
  itemDetails: {
    $base: {
      gap: 8,
      paddingLeft: 12,
    },
  },
  flex1: {
    $base: { flex: 1 },
  },
  desktopProductSpacer: {
    $gteDesktop: {
      flexGrow: 1,
      minHeight: 24,
    },
  },
  desktopProductContent: {
    $gteDesktop: {
      width: '100%',
      gap: 8,
      alignItems: 'center',
      paddingHorizontal: 24,
      marginHorizontal: 'auto',
    },
  },
  desktopProductTitle: {
    $gteDesktop: {
      width: '78%',
      maxWidth: 400,
      height: 53,
    },
  },
  desktopProductSubtitle: {
    $gteDesktop: {
      width: '41%',
      maxWidth: 209,
      height: 22,
    },
  },
  desktopDescriptionLong: {
    $gteDesktop: {
      width: '100%',
      maxWidth: 508,
      height: 22,
    },
  },
  desktopDescriptionMedium: {
    $gteDesktop: {
      width: '73%',
      maxWidth: 370,
      height: 22,
    },
  },
  desktopDescriptionShort: {
    $gteDesktop: {
      width: '49%',
      maxWidth: 248,
      height: 22,
    },
  },
  productHeader: {
    $gteDesktop: {
      width: '100%',
      gap: 4,
      alignItems: 'center',
      paddingTop: 28,
    },
  },
  container: {
    $ltDesktop: {
      alignSelf: 'center',
      maxWidth: 540,
      width: '100%',
      height: '100%',
    },
    $gteDesktop: {
      flex: 1,
      justifyContent: 'space-around',
      height: '100%',
    },
  },
  content: {
    $ltDesktop: {
      width: '100%',
      maxWidth: 548,
      marginHorizontal: 'auto',
      alignItems: 'center',
    },
  },
  titleContainer: {
    $ltDesktop: {
      width: '100%',
      alignItems: 'center',
      marginBottom: 8,
    },
  },
  mobileCustomizationContent: {
    $ltDesktop: {
      width: '100%',
      paddingHorizontal: 16,
      paddingBottom: 16,
      gap: 24,
    },
  },
  skeletonSection: {
    $ltDesktop: {
      width: '100%',
      gap: 12,
    },
  },
  desktopCustomizationContent: {
    $gteDesktop: {
      width: '100%',
      paddingHorizontal: 16,
      paddingBottom: 16,
      gap: 24,
    },
  },
  desktopSkeletonSection: {
    $gteDesktop: {
      width: '100%',
      gap: 12,
    },
  },
  description: {
    $base: {
      width: '100%',
      minHeight: 92,
      paddingVertical: 8,
      paddingHorizontal: 24,
      gap: 8,
      alignSelf: 'flex-start',
    },
  },
  itemTitle: {
    $base: {
      width: 222,
      height: 34,
      borderRadius: 12,
      backgroundColor: tokens.colors.$houseLight,
    },
  },
  itemSubtitle: {
    $base: {
      width: 154,
      height: 22,
      borderRadius: 12,
      backgroundColor: tokens.colors.$houseLight,
    },
  },
  itemDescription: {
    $base: {
      flex: 1,
      width: 154,
      height: 16,
      borderRadius: 12,
      backgroundColor: tokens.colors.$houseLight,
      gap: 8,
    },
  },
  itemDescriptionLine: {
    $base: {
      width: 154,
      height: 16,
      borderRadius: 12,
      backgroundColor: tokens.colors.$houseLight,
    },
  },
  sizeContainer: {
    $base: {
      width: '100%',
    },
  },
  sectionTitle: {
    $base: {
      width: 61,
      borderRadius: 12,
      backgroundColor: tokens.colors.$houseLight,
      alignSelf: 'flex-start',
    },
    $ltDesktop: {
      height: 20,
    },
    $gteDesktop: {
      height: 18,
    },
  },
  sizeBox: {
    $base: {
      flex: 1,
      backgroundColor: tokens.colors.$houseLight,
    },
    $ltDesktop: {
      height: 128,
      borderRadius: 8,
    },
    $gteDesktop: {
      height: 121,
      borderRadius: 12,
    },
  },
  sizeTile: {
    $base: {
      width: '100%',
    },
    $ltDesktop: {
      gap: 16,
    },
    $gteDesktop: {
      gap: 8,
    },
  },
  customizeHeader: {
    $base: {
      width: '100%',
      justifyContent: 'space-between',
    },
  },
  allowCombo: {
    $base: {
      alignItems: 'center',
      gap: 8,
    },
  },
  customizeSection: {
    $base: {
      gap: 8,
    },
  },
  customizeBox: {
    $ltDesktop: {
      width: '100%',
      height: 381,
      borderRadius: 8,
      backgroundColor: tokens.colors.$houseLight,
      borderColor: tokens.colors.$houseLight,
      borderWidth: 1,
    },
    $gteDesktop: {
      width: '100%',
      borderColor: tokens.colors.$blackOpacity10,
      borderWidth: 1,
      borderRadius: 8,
    },
  },
  customizeBoxDesktopRow: {
    $gteDesktop: {
      padding: 12,
      gap: 12,
      justifyContent: 'space-between',
      alignItems: 'center',
      height: 66,
    },
  },
  containerImageBackground: {
    $ltDesktop: {
      minHeight: 200,
      width: '100%',
      paddingTop: 30,
      overflow: 'hidden',
    },
  },
  backgroundContainer: {
    $gteDesktop: {
      height: 406,
      justifyContent: 'flex-end',
      width: '100%',
    },
  },
  imageAnatomy: {
    $base: {
      height: 121,
      width: 222,
      backgroundColor: tokens.colors.$houseLight,
      borderRadius: 12,
      alignSelf: 'center',
      bottom: 0,
    },
    $gteDesktop: {
      top: 0,
      height: 344,
      maxWidth: 469,
      width: '100%',
      position: 'absolute',
    },
  },
  comboSection: {
    $base: {
      width: '100%',
    },
  },
  comboTitle: {
    $base: {
      width: '45%',
      maxWidth: 180,
      height: 16,
      borderRadius: 8,
      backgroundColor: tokens.colors.$houseLight,
    },
  },
  comboItemsWrapper: {
    $base: {
      width: '100%',
      marginTop: 12,
      borderWidth: 1,
      borderColor: tokens.colors.$blackOpacity10,
      borderRadius: 8,
    },
  },
  comboItem: {
    $base: {
      alignItems: 'center',
      gap: 12,
      paddingVertical: 12,
      paddingHorizontal: 16,
    },
  },
  comboItemBorder: {
    $base: {
      borderBottomWidth: 1,
      borderBottomColor: tokens.colors.$blackOpacity10,
    },
  },
  comboItemImage: {
    $base: {
      width: 40,
      height: 40,
      borderRadius: 20,
      backgroundColor: tokens.colors.$houseLight,
    },
  },
  comboItemText: {
    $base: {
      flex: 1,
      gap: 6,
    },
  },
  comboItemTitle: {
    $base: {
      width: '55%',
      height: 16,
      borderRadius: 8,
      backgroundColor: tokens.colors.$houseLight,
    },
  },
  comboItemSubtitle: {
    $base: {
      width: '30%',
      height: 12,
      borderRadius: 8,
      backgroundColor: tokens.colors.$houseLight,
    },
  },
  contentDivider: {
    $base: {
      width: '100%',
      height: 1,
      backgroundColor: tokens.colors.$blackOpacity04,
    },
  },
  nutritionItem: {
    $base: {
      minHeight: 65,
      width: '100%',
      paddingVertical: 12,
      paddingHorizontal: 16,
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      borderWidth: 1,
      borderColor: tokens.colors.$blackOpacity10,
      borderRadius: 8,
    },
  },
  nutritionTitle: {
    $base: {
      width: '52%',
      maxWidth: 220,
      height: 16,
      borderRadius: 8,
      backgroundColor: tokens.colors.$houseLight,
    },
  },
  nutritionChevron: {
    $base: {
      width: 24,
      height: 24,
      borderRadius: 12,
      backgroundColor: tokens.colors.$houseLight,
    },
  },
  button: {
    $base: {
      flex: 1,
      maxWidth: 336,
    },
    $gteDesktop: {
      right: 26,
    },
  },
  buttonContent: {
    $base: {
      flexDirection: 'row',
      alignItems: 'center',
      flexShrink: 1,
      alignSelf: 'stretch',
      minWidth: 0,
      position: 'relative',
    },
  },
  desktopButtonContent: {
    $gteDesktop: {
      flex: 1,
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      paddingHorizontal: 12,
      position: 'relative',
    },
  },
  buttonText: {
    $base: {
      fontSize: 16,
      flexShrink: 0,
    },
  },
  buttonPriceSkeleton: {
    $base: {
      height: 16,
      width: 80,
      alignSelf: 'center',
    },
  },
  mobileButtonPrice: {
    $ltDesktop: {
      width: 64,
      flexShrink: 1,
      marginLeft: 4,
    },
  },
  footerContainer: {
    $ltDesktop: {
      backgroundColor: tokens.colors.$white,
      paddingTop: 16,
      paddingHorizontal: 16,
      paddingBottom: 8,
      alignSelf: 'center',
      width: '100%',
      borderTopWidth: 1,
      borderTopColor: tokens.colors.$blackOpacity10,
      maxWidth: 540,
    },
    $gteDesktop: {
      borderTopWidth: 1,
      borderTopColor: tokens.colors.$blackOpacity10,
      backgroundColor: tokens.colors.$white,
      alignSelf: 'center',
      width: '100%',
      padding: 16,
      paddingBottom: 8,
    },
  },
  footer: {
    $base: {
      flexDirection: 'row',
      alignSelf: 'center',
      justifyContent: 'center',
      width: '100%',
      gap: 28,
    },
    $gteDesktop: {
      justifyContent: 'flex-end',
      maxWidth: 1440,
      gap: 52,
      paddingRight: 52,
    },
  },
  itemImage: {
    $gteDesktop: {
      width: 40,
      height: 40,
      backgroundColor: tokens.colors.$houseLight,
      borderRadius: 4,
    },
  },
  selectBox: {
    $gteDesktop: {
      width: 24,
      height: 24,
      backgroundColor: tokens.colors.$houseLight,
      borderRadius: 4,
      padding: 12,
    },
  },
  divider: {
    $base: {
      height: 1,
      width: '100%',
      backgroundColor: tokens.colors.$houseLight,
    },
  },
});
